<template>
  <div class="ui-table" :class="{ 'ui-table--domains': domainsTable }">
    <div v-if="useSearch" class="search">
      <ui-input
        class="mt-8 mb-8"
        v-model="search"
        prepend-inner-icon="fas fa-search"
        type="text"
        :placeholder="t('Search')"
        :hide-details="1"
      />
    </div>
    <div class="table-wrapper">
      <div class="ui-table__inner">
        <div class="table-header">
          <div
            v-for="header in headers"
            :key="header.value"
            class="table-cell"
            :class="{ 'table-cell_sortable': header.sortable }"
            @click="onChangeSort(header)"
          >
            <div class="table-cell__inner">
              <div>{{ headerLabel(header) }}</div>
              <ui-tooltip-icon
                v-if="header.help"
                class="ml-4"
                preset="help"
                :text="header.help"
              />
              <div v-if="header.sortable" @click.stop="onChangeSort(header)">
                <ui-icon
                  :size="16"
                  :color="sortColumnKey === header.value ? colors.sunglow : colors.gray20"
                  :icon="`fas fa-caret-${sortDirection === 'asc' ? 'up' : 'down'}`"
                  class="sort-icon"
                  :class="{ 'active-chevron': sortColumnKey === header.value }"
                />
              </div>
            </div>
          </div>
          <div
            v-if="isTableBodyScrollVisible"
            class="table-scrollbar-gutter"
            :style="scrollbarGutterStyle"
          />
        </div>
        <div
          v-if="tableItems.length && !loading"
          ref="tableBodyRef"
          class="table-body"
          :style="{
            overflow: tableBodyOverflow,
            maxHeight: `${maxHeight}px`,
          }"
        >
          <template v-if="useRowVirtualization">
            <Virtualize
              v-for="(rowChunk, chunkIndex) in separatedItems"
              :key="chunkIndex"
            >
              <template #virtual-item>
                <div v-for="row in rowChunk" :key="stableRowKey(row)" class="table-row">
                  <template v-if="slots.items">
                    <slot name="items" :item="row" />
                  </template>
                  <template v-else>
                    <div
                      v-for="cellHeader in headers"
                      :key="cellHeader.value"
                      class="table-cell"
                    >
                      <slot :name="`item.${cellHeader.value}`" :item="row">
                        {{ cellValue(row, cellHeader.value) }}
                      </slot>
                    </div>
                  </template>
                  <div
                    v-if="isTableBodyScrollVisible"
                    class="table-scrollbar-gutter"
                    :style="scrollbarGutterStyle"
                  />
                </div>
              </template>
            </Virtualize>
          </template>
          <template v-else>
            <div v-for="row in tableItems" :key="stableRowKey(row)" class="table-row">
              <template v-if="slots.items">
                <slot name="items" :item="row" />
              </template>
              <template v-else>
                <div
                  v-for="cellHeader in headers"
                  :key="cellHeader.value"
                  class="table-cell"
                >
                  <slot :name="`item.${cellHeader.value}`" :item="row">
                    {{ cellValue(row, cellHeader.value) }}
                  </slot>
                </div>
              </template>
              <div
                v-if="isTableBodyScrollVisible"
                class="table-scrollbar-gutter"
                :style="scrollbarGutterStyle"
              />
            </div>
          </template>
        </div>
        <div v-if="appendRowSlotName" class="ui-table__append-footer">
          <slot :name="appendRowSlotName" />
        </div>
      </div>
      <div v-if="!tableItems.length && !loading" class="table-empty-state">
        <ui-not-found />
      </div>
      <loading-circle
        v-if="loading"
        width="32"
        height="32"
        class="table-wrapper__loading"
      />
    </div>
    <Pagination
      v-if="showPaginationVisible"
      :page="page"
      :rows-per-page-count="rowsPerPageCountLocal"
      :items-count="paginationItemsCount"
      @update:page="onPageUpdate"
      @update:rows-per-page-count="onRowsPerPageUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  watch,
} from 'vue';
import { chunk, orderBy } from 'lodash';
import Pagination from './pagination.vue';
import Virtualize from '@/components/virtualize/virtualize.vue';
import UiNotFound from '@/components/ui/ui-not-found.vue';
import scrollbarWidthPx from '@/components/helpers/scrollbar-width';
import LoadingCircle from '@/components/loading-circle.vue';
import { useI18n, useColors } from '@/composables';

export interface UiTableHeader {
  /** @deprecated prefer `title` — both are supported */
  text?: string;
  title?: string;
  value: string;
  align?: string;
  help?: string;
  sortable?: boolean;
}

export type UiTableFetchPagePayload = {
  itemsPerPage: number;
  page: number;
  sortBy?: { key: string; order: string }[];
};

const props = withDefaults(
  defineProps<{
    headers?: UiTableHeader[];
    useSearch?: boolean;
    items?: unknown[];
    loading?: boolean;
    tableBodyOverflow?: string;
    sortBy?: string | Array<{ key: string; order?: string }>;
    rowsPerPageCount?: number | 'all';
    searchBy?: string;
    maxHeight?: number;
    showPagination?: boolean;
    alwaysShowPagination?: boolean;
    virtualize?: boolean;
    domainsTable?: boolean;
    fetchPage?: (payload: UiTableFetchPagePayload) => Promise<void>;
    totalItems?: number;
  }>(),
  {
    headers: () => [],
    useSearch: true,
    items: () => [],
    loading: false,
    tableBodyOverflow: 'auto',
    rowsPerPageCount: 5,
    searchBy: 'name',
    maxHeight: 500,
    showPagination: true,
    alwaysShowPagination: false,
    virtualize: true,
    domainsTable: false,
  },
);

const emit = defineEmits<{
  'update:rowsPerPageCount': [newRowsPerPage: number | string];
}>();

const { t } = useI18n();
const colors = useColors();
const slots = useSlots();

const appendRowSlotName = computed((): 'append-row' | 'appendRow' | null => {
  if (typeof slots['append-row'] === 'function') {
    return 'append-row';
  }
  if (typeof slots.appendRow === 'function') {
    return 'appendRow';
  }
  return null;
});

const headerLabel = (header: UiTableHeader) => header.text ?? header.title ?? '';

const isServerTable = computed(() => typeof props.fetchPage === 'function');

const useRowVirtualization = computed(() => props.virtualize && !isServerTable.value);

const tableBodyRef = ref<HTMLElement | null>(null);
const resizeObserver = ref<ResizeObserver | null>(null);
const tableBodyRefInterval = ref<ReturnType<typeof setInterval> | null>(null);
const isScrollVisible = ref(false);

const page = ref(1);
const search = ref('');
const sortDirection = ref<'asc' | 'desc'>('asc');
const sortColumnKey = ref<string | undefined>(undefined);

/** Local rows-per-page so the table updates when the parent does not use v-model (e.g. passes a constant). */
const rowsPerPageCountLocal = ref<number | 'all'>(props.rowsPerPageCount);

watch(
  () => props.rowsPerPageCount,
  nextRowsPerPage => {
    rowsPerPageCountLocal.value = nextRowsPerPage;
  },
);

watch(
  () => props.sortBy,
  sortByFromParent => {
    if (Array.isArray(sortByFromParent) && sortByFromParent.length > 0) {
      const first = sortByFromParent[0];
      sortColumnKey.value = first.key;
      sortDirection.value = first.order === 'desc' ? 'desc' : 'asc';
      return;
    }
    if (typeof sortByFromParent === 'string' && sortByFromParent) {
      sortColumnKey.value = sortByFromParent;
      return;
    }
    sortColumnKey.value = undefined;
  },
  { immediate: true },
);

watch(search, () => {
  if (page.value !== 1) {
    page.value = 1;
  }
});

watch([sortColumnKey, sortDirection], () => {
  if (page.value !== 1) {
    page.value = 1;
  }
});

const sortedItems = computed(() => {
  const list = props.items ?? [];
  if (isServerTable.value) {
    return list;
  }
  if (!sortColumnKey.value) {
    return list;
  }
  return orderBy(list, sortColumnKey.value, sortDirection.value);
});

const tableItemsWithSearch = computed(() => {
  if (!search.value) {
    return sortedItems.value;
  }
  return sortedItems.value.filter(row => {
    const searchField = (row as Record<string, unknown>)[props.searchBy];
    return typeof searchField === 'string' && searchField.startsWith(search.value);
  });
});

const clampPage = () => {
  const perPage = rowsPerPageCountLocal.value;
  if (perPage === 'all') {
    page.value = 1;
    return;
  }
  const lastPage = Math.max(1, Math.ceil(tableItemsWithSearch.value.length / perPage));
  if (page.value > lastPage) {
    page.value = lastPage;
  }
};

watch(
  [() => tableItemsWithSearch.value.length, rowsPerPageCountLocal],
  () => clampPage(),
);

const tableItems = computed(() => {
  if (isServerTable.value) {
    return tableItemsWithSearch.value;
  }
  if (rowsPerPageCountLocal.value === 'all' || !props.showPagination) {
    return tableItemsWithSearch.value;
  }
  const rowsPerPage = rowsPerPageCountLocal.value as number;
  const start = (page.value - 1) * rowsPerPage;
  const end = page.value * rowsPerPage;
  return tableItemsWithSearch.value.slice(start, end);
});

const paginationItemsCount = computed(() =>
  isServerTable.value ? (props.totalItems ?? 0) : tableItemsWithSearch.value.length,
);

const showPaginationVisible = computed(() => {
  if (!props.showPagination || props.loading) {
    return false;
  }
  const total = paginationItemsCount.value;
  if (total <= 0) {
    return false;
  }
  const perPageRaw = rowsPerPageCountLocal.value;
  if (perPageRaw === 'all') {
    return true;
  }
  if (props.alwaysShowPagination) {
    return true;
  }
  const perPage = perPageRaw as number;
  return total > perPage;
});

const runFetchPage = async () => {
  if (!props.fetchPage) {
    return;
  }
  const perPageRaw = rowsPerPageCountLocal.value;
  const itemsPerPage =
    perPageRaw === 'all' ? Math.max(props.totalItems ?? 0, 1) : (perPageRaw as number);
  await props.fetchPage({
    itemsPerPage,
    page: page.value,
    sortBy:
      sortColumnKey.value !== undefined && sortColumnKey.value !== ''
        ? [{ key: sortColumnKey.value, order: sortDirection.value }]
        : [],
  });
};

watch(
  [page, rowsPerPageCountLocal, sortColumnKey, sortDirection],
  () => {
    if (!props.fetchPage) {
      return;
    }
    void runFetchPage();
  },
  { flush: 'post', immediate: true },
);

const separatedItems = computed(() => chunk(tableItems.value, 40));

const isTableBodyScrollVisible = computed(
  () => !!scrollbarWidthPx && isScrollVisible.value,
);

const scrollbarGutterStyle = computed(() => ({
  width: `${scrollbarWidthPx + 4}px`,
  maxWidth: `${scrollbarWidthPx + 4}px`,
  minWidth: `${scrollbarWidthPx + 4}px`,
}));

const cellValue = (row: unknown, columnKey: string): string => {
  if (row && typeof row === 'object' && columnKey in row) {
    const rawCell = (row as Record<string, unknown>)[columnKey];
    if (rawCell === undefined || rawCell === null) {
      return '';
    }
    return String(rawCell);
  }
  return '';
};

const onChangeSort = (header: UiTableHeader) => {
  if (!header.sortable) {
    return;
  }
  if (sortColumnKey.value === header.value) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumnKey.value = header.value;
    sortDirection.value = 'asc';
  }
};

const stableRowKey = (row: unknown): string => {
  const record = row as Record<string, unknown>;
  if (record.id !== undefined && record.id !== null) {
    return String(record.id);
  }
  return JSON.stringify(
    Object.keys(record)
      .sort()
      .map(propertyKey => [propertyKey, record[propertyKey]]),
  );
};

const onPageUpdate = (nextPage: number) => {
  page.value = nextPage;
};

const onRowsPerPageUpdate = (newRowsPerPage: number | string) => {
  const normalized = newRowsPerPage === 'all' ? 'all' : Number(newRowsPerPage);
  rowsPerPageCountLocal.value = normalized;
  emit('update:rowsPerPageCount', normalized);
};

const updateBodyScrollVisible = () => {
  const tableBodyElement = tableBodyRef.value;
  if (!tableBodyElement) {
    isScrollVisible.value = false;
    return;
  }
  const { clientHeight, scrollHeight } = tableBodyElement;
  if (clientHeight < 1) {
    isScrollVisible.value = false;
    return;
  }
  // Avoid phantom gutter when clientHeight is 0 before layout, or subpixel noise.
  isScrollVisible.value = scrollHeight > clientHeight + 1;
};

onMounted(() => {
  resizeObserver.value = new ResizeObserver(() => {
    updateBodyScrollVisible();
  });

  tableBodyRefInterval.value = setInterval(() => {
    if (tableBodyRef.value) {
      resizeObserver.value?.observe(tableBodyRef.value);
      if (tableBodyRefInterval.value) {
        clearInterval(tableBodyRefInterval.value);
        tableBodyRefInterval.value = null;
      }
    }
  }, 50);
});

watch(
  [tableItems, () => props.loading, () => props.maxHeight],
  () => {
    nextTick(() => updateBodyScrollVisible());
  },
  { flush: 'post' },
);

onBeforeUnmount(() => {
  if (tableBodyRef.value) {
    resizeObserver.value?.unobserve(tableBodyRef.value);
  }
  resizeObserver.value?.disconnect();
  if (tableBodyRefInterval.value) {
    clearInterval(tableBodyRefInterval.value);
  }
});
</script>

<style lang="scss" scoped>
.table-cell_sortable {
  cursor: pointer;
}

.table-header .table-cell .sort-icon {
  visibility: hidden;
  position: relative;
  bottom: -2px;
  line-height: 9px;
  margin-left: 2px;
}

.table-header .table-cell .sort-icon.active-chevron {
  visibility: visible;
}

.table-header .table-cell:hover .sort-icon {
  visibility: visible;
}

.search {
  width: 200px;
}

.ui-table {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.table-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: auto;
  box-sizing: border-box;
  min-height: 80px;
}

.ui-table__inner {
  display: table;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  table-layout: auto;
  border-collapse: separate;
  border-spacing: 0;
}

.ui-table__inner .table-header {
  display: table-row;
  height: 32px;
  border-bottom: 1px solid var(--color-gray10);

  .table-cell {
    font-weight: 600;
  }

  .table-cell__inner {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
}

.ui-table__inner .table-header > .table-cell,
.ui-table__inner .table-header > .table-scrollbar-gutter {
  display: table-cell;
  height: 32px;
  vertical-align: middle;
}

.ui-table__inner .table-body {
  display: table-row-group;
  overflow: auto;
  border-bottom: 1px solid var(--color-gray10);
}

.ui-table__inner .ui-table__append-footer {
  display: table-footer-group;
}

.ui-table__inner .ui-table__append-footer :deep(.table-row) {
  display: table-row;
}

.ui-table__inner .ui-table__append-footer :deep(.table-row .table-cell),
.ui-table__inner .ui-table__append-footer :deep(.table-row .table-scrollbar-gutter) {
  display: table-cell;
  vertical-align: middle;
}

.ui-table__inner .ui-table__append-footer :deep(.table-row > .table-cell) {
  border-top: 1px solid var(--color-gray10);
}

.ui-table__inner .table-body > div:not(.table-row) {
  display: contents;
}

.ui-table__inner .table-body > .table-row {
  display: table-row;
}

.ui-table__inner .table-body :deep(.table-row) {
  display: table-row;
  flex-direction: row;
}

.ui-table__inner .table-body :deep(.table-row .table-cell),
.ui-table__inner .table-body :deep(.table-row .table-scrollbar-gutter) {
  display: table-cell;
  vertical-align: middle;
}

.ui-table__inner :deep(.table-cell) {
  flex: none;
  min-width: 0;
}

.table-body :deep(.table-row) > div {
  border-top: 1px solid var(--color-gray10);
}

.table-empty-state {
  width: 100%;
  box-sizing: border-box;
  border-top: 1px solid var(--color-gray10);
  padding-top: 8px;

  :deep(.not-found .ui-menu-content__list-item-text) {
    text-transform: uppercase;
  }
}

.table-body :deep(.table-row:hover) {
  background: var(--color-gray5);
  cursor: pointer;
}

.table-wrapper :deep(.table-cell) {
  flex: 1;
  font-size: 13px;
  line-height: 13px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.ui-table__inner .table-header > .table-cell {
  padding: 0 10px;
}

.ui-table__inner .table-header > .table-cell:first-child {
  padding-left: 0;
}

.ui-table__inner .table-body :deep(.table-row .table-cell) {
  height: 34px;
  padding: 8px 10px;
  box-sizing: border-box;
}

.ui-table__inner .table-body :deep(.table-row .table-cell:first-child) {
  padding-left: 0;
}

.ui-table--domains .ui-table__inner .table-body :deep(.table-row .table-cell) {
  padding: 8px 16px 8px 0;
}

.table-wrapper__loading {
  margin-top: 20px;
  margin-bottom: 20px;
}
.ui-table.custom_pagination {
  .v-input__control {
    width: fit-content;
  }

  .v-btn {
    height: 26px !important;

    &--icon {
      width: 26px;
    }
  }

  .v-icon {
    font-size: 16px;
  }

  .v-datatable__actions__select {
    max-width: 200px;

    .v-select {
      min-width: 60px;

      .v-select__selections {
        padding-left: 8px;
      }
    }

    .v-input__append-inner {
      top: 2px !important;
    }
  }
}

.table-no-border-right {
  .table-empty-state {
    border-top: 1px solid var(--color-gray10) !important;
    padding-top: 8px;
    width: 100%;
  }

  .ui-table__inner {
    .table-header {
      border-top: none;
      border-bottom: 0;

      .table-cell {
        color: var(--color-graphite);
        padding-left: 0 !important;
        padding-right: 0 !important;
        border: none;
      }
    }

    .table-body :deep(.table-row) > .table-cell {
      border-top: 1px solid var(--color-gray10) !important;
    }

    .table-body :deep(.table-row .table-cell) {
      border-right: 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
      text-align: center;
      color: var(--color-graphite);

      &:first-child {
        text-align: left;
      }
    }
  }

  .v-datatable {
    border: none !important;

    thead tr {
      border-top: none !important;
      border-bottom: 0px !important;

      th {
        color: var(--color-graphite) !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        border: none !important;
      }

      .v-icon {
        margin: 0 !important;
        position: relative;
        bottom: -3px;
      }
    }

    tbody {
      tr {
        border-top: 1px solid var(--color-gray10) !important;

        td:first-child {
          text-align: left;
        }
      }

      tr td {
        border-right: 0px !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        text-align: center;
        color: var(--color-graphite);

        td {
          display: inline-block;
        }
      }

      &:last-child tr {
        border-bottom: none !important;
      }
    }

    tfoot {
      tr {
        color: var(--color-graphite) !important;

        td {
          text-align: center;

          td {
            display: inline;
          }
        }

        td:first-child {
          text-align-last: left;
          padding-left: 0;
        }
      }
    }

    thead tr:first-child,
    tfoot tr:first-child {
      height: 32px !important;
      border-bottom: 0 solid var(--color-gray10) !important;
      background: var(--color-white);
      color: var(--color-white);
    }
  }
}
</style>
